import { useMemo } from "react";
import { Flame, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useUserStats } from "@/hooks/useUserStats";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { levelForXp, xpToNextLevel, xpForLevel, formatXp } from "@/lib/xp";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Today's XP earned, derived from items resolved today + their xp_value.
// `user_stats` doesn't persist daily XP separately, so we compute it from
// items. Single query per day; query-key includes the date so it auto-busts
// at midnight.
function useTodayStats() {
  const { user } = useAuth();
  const today = new Date().toISOString().split("T")[0];
  return useQuery({
    queryKey: ["today-stats", user?.id, today],
    queryFn: async () => {
      if (!user) return { xp: 0, count: 0 };
      const start = `${today}T00:00:00`;
      const { data, error } = await supabase
        .from("items")
        .select("xp_value")
        .eq("user_id", user.id)
        .eq("status", "done")
        .gte("resolved_at", start);
      if (error) throw error;
      const rows = data ?? [];
      const xp = rows.reduce(
        (sum, r) => sum + ((r as { xp_value: number | null }).xp_value ?? 0),
        0,
      );
      return { xp, count: rows.length };
    },
    enabled: !!user,
    staleTime: 30_000,
  });
}

export function StatsBar() {
  const { data: stats } = useUserStats();
  const { data: today } = useTodayStats();

  // Always derive level from XP — `user_stats.level` only updates via
  // useAwardXP, so any other XP write path (admin SQL, manual fix) leaves
  // it stale. The XP curve is the source of truth.
  const xp = stats?.xp ?? 0;
  const level = useMemo(() => levelForXp(xp), [xp]);
  const { current: xpIntoLevel, next: xpForLevelSpan, progress } = useMemo(
    () => xpToNextLevel(xp),
    [xp],
  );
  const levelStartXp = xpForLevel(level);
  const levelEndXp = xpForLevel(level + 1);

  if (!stats) return null;

  const dailyTarget = stats.daily_target;
  const dailyDone = stats.daily_completed;
  const hitGoal = dailyDone >= dailyTarget && dailyTarget > 0;
  const dailyProgress = dailyTarget > 0 ? dailyDone / dailyTarget : 0;

  // Stretch tier kicks in once the daily floor is hit. 2× the floor — a real
  // "good day" pace for anyone who blew through 25 by 9am.
  const stretchTarget = dailyTarget * 2;
  const stretchProgress =
    hitGoal && stretchTarget > 0
      ? Math.min((dailyDone - dailyTarget) / (stretchTarget - dailyTarget), 1)
      : 0;
  const hitStretch = dailyDone >= stretchTarget;

  const circumference = 2 * Math.PI * 18;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-4 mb-4 px-1">
        {/* Level badge with XP ring + hover tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="relative w-12 h-12 shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={`Level ${level}, ${formatXp(xpIntoLevel)} of ${formatXp(xpForLevelSpan)} XP to next level`}
            >
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 40 40">
                <circle
                  cx="20"
                  cy="20"
                  r="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-muted/30"
                />
                <motion.circle
                  cx="20"
                  cy="20"
                  r="18"
                  fill="none"
                  strokeWidth="2.5"
                  className="text-primary"
                  stroke="currentColor"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference * (1 - progress)}
                  strokeLinecap="round"
                  initial={false}
                  animate={{ strokeDashoffset: circumference * (1 - progress) }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold">{level}</span>
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="px-3 py-2.5 max-w-xs">
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center justify-between gap-4">
                <span className="font-semibold text-sm">Level {level}</span>
                <span className="font-mono opacity-80">{formatXp(xp)} XP</span>
              </div>
              <div className="opacity-70">
                {xpIntoLevel.toLocaleString()} / {xpForLevelSpan.toLocaleString()} XP into level
                <br />
                {(levelEndXp - xp).toLocaleString()} XP to level {level + 1}
              </div>
              <div className="pt-1 border-t border-primary-foreground/15 opacity-70">
                Current threshold: {levelStartXp.toLocaleString()}
                <br />
                Next threshold: {levelEndXp.toLocaleString()}
                <br />
                Lifetime items: {stats.total_completed.toLocaleString()}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>

        {/* Streak */}
        <div className="flex items-center gap-1.5">
          <Flame
            className={`h-4 w-4 ${stats.streak_days > 0 ? "text-orange-500" : "text-muted-foreground/40"}`}
          />
          <span className="text-xs font-medium">
            {stats.streak_days > 0 ? `${stats.streak_days}d streak` : "No streak"}
          </span>
        </div>

        {/* Daily progress — three states:
            1. Under daily floor: standard X/Y progress.
            2. Floor hit, under stretch: ✓ goal chip + stretch bar to 2× floor.
            3. Stretch hit: ✓ goal chip + "Beast mode" + today's XP.
            Once the floor is cleared we replace the dead-full bar with the
            metric that actually still moves — XP earned today. */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {!hitGoal ? (
            <>
              <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                {dailyDone}/{dailyTarget}
              </span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary rounded-full"
                  initial={false}
                  animate={{ width: `${Math.min(dailyProgress, 1) * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </>
          ) : hitStretch ? (
            <>
              <Trophy className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
              <span className="text-xs font-medium shrink-0">Beast mode</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {today ? `+${formatXp(today.xp)} XP today` : `${dailyDone} done`}
              </span>
            </>
          ) : (
            <>
              <span
                className="inline-flex items-center gap-1 text-xs font-medium text-primary shrink-0"
                aria-label="Daily goal cleared"
              >
                <svg
                  viewBox="0 0 16 16"
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 8l3.5 3.5L13 5" />
                </svg>
                Goal
              </span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-yellow-500/80 rounded-full"
                  initial={false}
                  animate={{ width: `${stretchProgress * 100}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                {today ? `+${formatXp(today.xp)} XP` : `${dailyDone}/${stretchTarget}`}
              </span>
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
